import loadjs from 'loadjs';
import { jsonp } from './init';
import { relative } from './utils/relate';
import { appInfo } from '@weapp/utils';

export interface Obj {
  [key: string]: any
}

const externals: Obj = {
  'react': 'React',
  'react-dom': 'ReactDOM',
  'react-router-dom': 'ReactRouterDOM',
  'axios': 'axios',
  'mobx': 'mobx',
  'mobx-react': 'mobxReact',
  'mobx-react-lite': 'mobxReactLite',
  'loadjs': 'loadjs',
}

const fixExtension = (path: string) => {
  return (!path || /\.js$/.test(path)) ? path : `${path}.js`;
}

jsonp.export = (appId: string, filePath: string, moduleExports: () => any) => {
  if (appId && /^[^.]/.test(filePath) && typeof moduleExports === 'function') {
    if (!jsonp[appId]) {
      jsonp[appId] = {};
    }
    jsonp[appId][filePath] = moduleExports;
    return;
  }
  console.error(`Ecode export module failed: appId "${appId}", filePath "${filePath}"`)
};

jsonp.require = (appId: string, filePath: string, relativePath?: string) => {
  if (appId && filePath && /^[^.]/.test(filePath)) {
    filePath = fixExtension(filePath);
    if (jsonp[appId] && (!relativePath || /^\./.test(relativePath))) {
      const requirePath = relative(filePath, fixExtension(relativePath || '')) || filePath;
      const mod = jsonp[appId]?.[requirePath];
      // 循环引用返回 undefined 处理
      if (mod && mod.fetching) {
        console.error(`Recycling applications are not supported.
Cannot access [appId "${appId}", filePath "${filePath}", relativePath "${relativePath}"] before initialization.`);
        return {};
      }
      if (mod && mod.exports) {
        return mod.exports;
      }
      if (typeof mod === 'function') {
        jsonp[appId][requirePath].fetching = true;
        const modExports = mod(jsonp.require);
        jsonp[appId][requirePath].exports = modExports;
        delete jsonp[appId][requirePath].fetching;
        return modExports;
      }
    } else if (relativePath){
      if (/^@weapp\//.test(relativePath)) {
        const mod = window[appInfo(relativePath).libraryName];
        if (mod) {
          return mod;
        }
      } else if (externals[relativePath]) {
        const mod = window[externals[relativePath]];
        if (mod) {
          return mod;
        }
      }
    }
  }
  console.error(`Ecode require module failed: appId "${appId}", filePath "${filePath}", relativePath "${relativePath}"`)
}

export interface TempObj {
  key: string;
  resolve: (reason?: any) => void;
  reject: (reason?: any) => void;
}
export interface Temp {
  [key: string]: TempObj[];
}

export interface LoadedCache {
  [key: string]: boolean;
}

const temp: Temp = {};

const loadedCache: LoadedCache = {};

// 第一次请求成功前，重复请求挂起
function dispath(appId: string, action: 'resolve' | 'reject', err?: any) {
  if (temp[appId].length) {
    while(temp[appId].length) {
      let result = err;
      if (action === 'resolve') {
        const filePath = temp[appId][0].key;
        if (jsonp?.[appId]?.[filePath]) {
          result = jsonp.require(appId, filePath)
        } else {
          action = 'reject';
          console.error(result);
        }
      }
      temp[appId][0][action](result);
      temp[appId].shift();
    }
  }
  loadedCache[appId] = true;
}

export const asyncImport = (appId: string, filePath: string, isSimpleMode?: boolean) => new Promise((resolve, reject) => {
  const error = `Ecode async import() failed: appId "${appId}", filePath "${filePath}"`;
  if (appId && /^[^.]/.test(filePath)) {
    filePath = fixExtension(filePath);
    // 成功加载过 chunk 并且 chunk 中有 filePath 模块，直接返回
    if (jsonp?.[appId]?.[filePath]) {
      resolve(jsonp.require(appId, filePath));
      return;
    }

    // 成功加载过 chunk 且其他 filePath 模块存在，则直接抛错当前 filePath 模块
    if (loadedCache[appId]) {
      reject(error);
      return;
    }
    // 占用 @weapp/ecode 的二级路径变量规则
    const { publicUrl } = appInfo('@weapp/ecode');
    const publicDomain = window['publicDomainfileEcode'] || '';
    const chunksPrefix = window.localStorage.getItem('__ecode_chunks_prefix__') || ''; 
    if (isSimpleMode && chunksPrefix) { // 简易模式 不需要对比缓存，直接加载请求
      // 正在执行后续代码
      if (temp[appId]) {
        temp[appId].push({ key: filePath, resolve, reject });
        return;
      }
      temp[appId] = [{ key: filePath, resolve, reject }];
      const loadKey = `__ecode_chunks_${appId}__`;
      jsonp[appId] = {
        chunks: [
          `${chunksPrefix}simple/${appId}/index.js?v=${new Date().getTime()}`,
          `${chunksPrefix}simple/${appId}/index.css?v=${new Date().getTime()}`
        ].map(chunk => `${publicDomain}${publicUrl}${chunk}`)
      }
      loadjs(jsonp[appId].chunks, loadKey);
      loadjs.ready(loadKey, {
        success: () => {
          dispath(appId, 'resolve', error);
        },
        error: (err: any) => {
          // console.error(errorLoad, err);
          dispath(appId, 'reject', `load simple app failed: appId "${appId}", "${jsonp[appId].chunks}"`);
        }
      });
      return;
    }
    // 没有加载过的模块但是有 chunks, 发起加载
    if (jsonp?.[appId]?.chunks) {
      const errorLoad = `load chunk failed: appId "${appId}", "${jsonp[appId].chunks}"`;
      const loadOptions = {
        success: () => {
          dispath(appId, 'resolve', error);
        },
        error: (err: any) => {
          // console.error(errorLoad, err);
          dispath(appId, 'reject', errorLoad);
        }
      };

      // 正在执行后续代码
      if (temp[appId]) {
        temp[appId].push({
          key: filePath,
          resolve, reject
        });
        return;
      }

      temp[appId] = [{
        key: filePath,
        resolve, reject
      }];

      const loadKey = `__ecode_chunks_${appId}__`;
      let chunks = jsonp[appId].chunks;
      if (Array.isArray(chunks) && chunks.length) {
        chunks = chunks.map(chunk => `${publicDomain}${publicUrl}${chunk}`);
      }
      loadjs(chunks, loadKey);
      loadjs.ready(loadKey, loadOptions);
      return;
    }
  }
  console.error(error);
  reject(error);
});

export { jsonp, relative };
